
import { Button } from '@/components/ui/button';
import { TrendingUp, BarChart3, DollarSign, Bot, Zap } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useScrollAnimation, useStaggeredAnimation } from '@/hooks/useScrollAnimation';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const headerAnimation = useScrollAnimation({ threshold: 0.01 });
  const product1Animation = useStaggeredAnimation(0);
  const product2Animation = useStaggeredAnimation(100);

  const products = [
    {
      name: "DUOCORE",
      subtitle: "Copy Trade Inteligente",
      performance: "+107,24%",
      description: "Dois algoritmos quantitativos trabalhando em sinergia",
      stats: {
        accuracy: "+60%",
        profit: "+107,24%",
        uptime: "5 minutos"
      },
      chart: [...Array(12)].map((_, i) => Math.random() * 80 + 20),
      color: "from-cyan-400 to-green-400"
    },
    {
      name: "ROBÔS PRONTOS",
      subtitle: "Estratégias Especializadas",
      performance: "+156,80%",
      description: "Robôs com estratégias específicas e resultados comprovados",
      stats: {
        accuracy: "89.7%",
        profit: "+156,80%",
        uptime: "Personalizável"
      },
      chart: [...Array(12)].map((_, i) => Math.random() * 60 + 30),
      color: "from-green-400 to-yellow-400"
    }
  ];

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % products.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const currentProduct = products[currentSlide];

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-50">
        <div 
          className="w-full h-full" 
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2364ffda' fill-opacity='0.1'%3E%3Ccircle cx='20' cy='20' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}
        />
      </div>
      
      {/* Floating Icons Animation */}
      <div className="absolute inset-0 overflow-hidden">
        <TrendingUp className="absolute top-20 left-10 text-cyan-400/20 w-8 h-8" />
        <BarChart3 className="absolute top-40 right-20 text-green-400/20 w-12 h-12 animate-bounce" />
        <DollarSign className="absolute bottom-40 left-20 text-cyan-400/20 w-10 h-10" />
        <Bot className="absolute top-60 right-10 text-yellow-400/20 w-10 h-10" />
        <Zap className="absolute bottom-20 right-40 text-green-400/20 w-8 h-8 animate-bounce" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Centered Header Content */}
        <div 
          ref={headerAnimation.ref}
          className={`text-center mb-16 scroll-animate-scale ${headerAnimation.isVisible ? 'visible' : ''}`}
        >
          <div className="mb-6 mt-12">
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-cyan-400/10 to-green-400/10 border border-cyan-400/20 rounded-full text-cyan-400 text-sm font-medium mb-4">
              🚀 Tecnologia Quantitativa Comprovada
            </span>
          </div>
          
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight sm:leading-[1.1] px-2 sm:px-0 max-w-5xl mx-auto">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-green-400">2 Sistemas de Trading Automatizado</span>
            <br className="hidden sm:inline" /> para{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-yellow-400">Lucrar com Consistência</span>
            <br className="hidden md:inline" /> e Eliminar o Erro Humano nas Operações
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-300 mb-6 leading-relaxed max-w-4xl mx-auto">
            <span className="text-white font-semibold">DUOCORE Copytrade</span> e <span className="text-white font-semibold">Robôs QuantMake</span> — desenvolvidos com mais de 10 anos de experiência em análise quantitativa e operações automatizadas, para quem busca performance profissional sem depender de decisões emocionais.
          </p>

          <div className="flex flex-wrap justify-center gap-2 text-sm text-cyan-400 mb-8">
            <span className="flex items-center gap-1">
              <span className="text-green-400">✓</span> Escolha o sistema ideal para seu perfil
            </span>
            <span className="hidden sm:inline">•</span>
            <span className="flex items-center gap-1">
              <span className="text-green-400">✓</span> Execução precisa e consistente
            </span>
            <span className="hidden sm:inline">•</span>
            <span className="flex items-center gap-1">
              <span className="text-green-400">✓</span> Setup em 5 minutos
            </span>
          </div>
        </div>

        {/* Two Product Blocks Side by Side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {products.map((product, index) => (
            <div 
              key={index} 
              ref={index === 0 ? product1Animation.ref : product2Animation.ref}
              className={`relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm rounded-2xl p-8 border border-slate-700 scroll-animate ${
                index === 0 ? (product1Animation.isVisible ? 'visible' : '') : (product2Animation.isVisible ? 'visible' : '')
              }`}
            >
              {/* Product Header */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className={`text-xl font-bold bg-gradient-to-r ${product.color} bg-clip-text text-transparent`}>
                    {product.name}
                  </h3>
                  <p className="text-slate-300 text-sm">{product.subtitle}</p>
                </div>
                <span className="text-green-400 font-bold text-2xl">{product.performance}</span>
              </div>

              {/* Product Description */}
              <p className="text-slate-300 text-sm mb-6">{product.description}</p>
              
              {/* Performance Chart */}
              <div className="mb-6">
                <h3 className="text-white font-medium text-sm mb-3">{index === 0 ? 'Performance Anual' : 'Performance Média'}</h3>
                <div className="flex items-end space-x-1 h-32 bg-slate-900/50 p-4 rounded-lg">
                  {product.chart.map((height, i) => (
                    <div key={i} className="flex flex-col items-center space-y-1">
                      <div 
                        className={`w-4 bg-gradient-to-t ${product.color} rounded-sm transition-all duration-500`}
                        style={{ height: `${height}px` }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center">
                  <div className="text-lg font-bold text-cyan-400">{product.stats.accuracy}</div>
                  <div className="text-xs text-slate-400">Taxa de Acerto</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-green-400">{product.stats.profit}</div>
                  <div className="text-xs text-slate-400">{index === 0 ? 'CAGR' : 'CAGR Médio'}</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-yellow-400">{product.stats.uptime}</div>
                  <div className="text-xs text-slate-400">Configuração</div>
                </div>
              </div>

              {/* Key Stats */}
              <div className="grid grid-cols-2 gap-4 text-sm mb-6">
                <div>
                  <div className="text-slate-400">{index === 0 ? 'Drawdown Máx.' : 'Diversificação'}</div>
                  <div className="text-white font-semibold">{index === 0 ? '18%' : 'Parâmetros'}</div>
                </div>
                <div>
                  <div className="text-slate-400">{index === 0 ? 'Operação' : 'Backtests'}</div>
                  <div className="text-white font-semibold">{index === 0 ? 'Simplificada' : 'Histórico Completo'}</div>
                </div>
              </div>

              {/* CTA Button */}
              <Button 
                size="lg" 
                className="w-full bg-gradient-to-r from-cyan-400 to-green-400 hover:from-cyan-500 hover:to-green-500 text-slate-900 font-bold text-lg px-8 py-3 rounded-full shadow-lg hover:shadow-cyan-400/25 transition-all duration-300 mb-6 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900"
                onClick={() => scrollToSection('#products')}
              >
                COMEÇAR AGORA! 🚀
              </Button>

              {/* Testimonial */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 bg-gradient-to-r ${product.color} rounded-full flex items-center justify-center`}>
                    <span className="text-slate-900 font-bold text-sm">
                      {index === 0 ? 'CM' : 'V'}
                    </span>
                  </div>
                  <div>
                    <div className="text-white font-medium">
                      {index === 0 ? 'Carlos M.' : 'Vico'}
                    </div>
                    <div className="text-slate-400 text-sm">
                      Cliente
                    </div>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mt-2">
                  {index === 0 
                    ? '"DUOCORE é a forma mais simples e rápida que eu já usei para fazer trading."'
                    : '"É gratificante operar com uma ferramenta tão bem estruturada e com acompanhamento tão próximo."'
                  }
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 text-center text-sm text-slate-400">
          ✅ Setup Automático • ✅ Suporte direto com os Especialistas • ✅ Resultados Comprovados
        </p>
      </div>
    </section>
  );
};

export default HeroSection;
